import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getApiHost } from '../../../../app/infrastructure/http/apiClient';
import { notificationsApi } from '../../../notification/infrastructure/api/notifications.api';
import {
  mapConversationListPage,
  mapConversationMessage,
  mapConversationMessagesPage,
  mapConversationResponse,
  unwrapData,
} from '../../application/mapConversation';
import type {
  Conversation,
  ConversationListPage,
  ConversationMessage,
  ConversationMessagesPage,
  StartConversationRequest,
} from '../../application/types';

function authorizationHeader(token?: string | null): string | undefined {
  if (!token) return undefined;
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
}

function apiBaseUrl(): string {
  const host = getApiHost();
  return host.endsWith('/') ? host : `${host}/`;
}

export const conversationApi = createApi({
  reducerPath: 'conversationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl(),
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const token = (
        getState() as { authorization?: { accessToken?: string | null } }
      ).authorization?.accessToken;
      const value = authorizationHeader(token);
      if (value) headers.set('Authorization', value);
      return headers;
    },
  }),
  tagTypes: ['Conversations', 'ConversationMessages'],
  endpoints: (build) => ({
    listConversations: build.query<
      ConversationListPage,
      { page?: number; limit?: number }
    >({
      query: ({ page = 1, limit = 50 }) => ({
        url: 'conversations',
        method: 'GET',
        params: { page, limit },
      }),
      transformResponse: mapConversationListPage,
      providesTags: (result) =>
        result
          ? [
              ...result.list.map((row) => ({
                type: 'Conversations' as const,
                id: row.id,
              })),
              { type: 'Conversations', id: 'LIST' },
            ]
          : [{ type: 'Conversations', id: 'LIST' }],
    }),
    getConversation: build.query<Conversation, number>({
      query: (id) => ({
        url: `conversations/${id}`,
        method: 'GET',
      }),
      transformResponse: mapConversationResponse,
      providesTags: (_result, _error, id) => [{ type: 'Conversations', id }],
    }),
    listConversationMessages: build.query<
      ConversationMessagesPage,
      { conversationId: number; limit?: number; beforeId?: number }
    >({
      query: ({ conversationId, limit, beforeId }) => ({
        url: `conversations/${conversationId}/messages`,
        method: 'GET',
        params: {
          limit: limit ?? 50,
          beforeId,
        },
      }),
      transformResponse: mapConversationMessagesPage,
      providesTags: (_result, _error, arg) => [
        { type: 'ConversationMessages', id: arg.conversationId },
      ],
    }),
    startConversation: build.mutation<Conversation, StartConversationRequest>({
      query: (body) => ({
        url: 'conversations',
        method: 'POST',
        body,
      }),
      transformResponse: mapConversationResponse,
      invalidatesTags: [{ type: 'Conversations', id: 'LIST' }],
    }),
    sendConversationMessage: build.mutation<
      ConversationMessage,
      { conversationId: number; body: string }
    >({
      query: ({ conversationId, body }) => ({
        url: `conversations/${conversationId}/messages`,
        method: 'POST',
        body: { body, messageType: 'text' },
      }),
      transformResponse: (response: unknown) =>
        mapConversationMessage(unwrapData(response)) ?? {
          id: 0,
          conversationId: 0,
          senderId: 0,
          messageType: 'text',
          body: '',
          createdAt: '',
          updatedAt: '',
        },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Conversations', id: 'LIST' },
        { type: 'Conversations', id: arg.conversationId },
        { type: 'ConversationMessages', id: arg.conversationId },
      ],
    }),
    markConversationRead: build.mutation<
      Conversation,
      { conversationId: number; messageId?: number }
    >({
      query: ({ conversationId, messageId }) => ({
        url: `conversations/${conversationId}/read`,
        method: 'PATCH',
        body: messageId == null ? {} : { messageId },
      }),
      transformResponse: mapConversationResponse,
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Conversations', id: 'LIST' },
        { type: 'Conversations', id: arg.conversationId },
      ],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              notificationsApi.util.invalidateTags(['NotificationsCount']),
            );
          })
          .catch(() => undefined);
      },
    }),
  }),
});

export const {
  useListConversationsQuery,
  useGetConversationQuery,
  useListConversationMessagesQuery,
  useStartConversationMutation,
  useSendConversationMessageMutation,
  useMarkConversationReadMutation,
} = conversationApi;
